export const CONVEYOR_COST = 1;
export const FROM_COST = 140;
export const FILTER_COST = 110;

/** Flat, and the cheapest operator with two inputs: `zip` only packs packets into a tuple — the
 * `map` that turns that tuple into a product is a second tile, priced by its recipe. */
export const ZIP_COST = 60;

/**
 * Deliberately many times `ZIP_COST`, because `combineLatest` spends nothing: a single remembered
 * carbon keeps pairing with every iron that follows it, so as a crafting machine it strictly beats
 * the join it is meant to contrast with. The price is what stops it from simply replacing `zip` the
 * moment it unlocks — it has to be bought back out of the streams it does not consume.
 */
export const COMBINE_LATEST_COST = 480;

/**
 * Priced like a label rather than a machine: a Subject transforms nothing, holds nothing and moves
 * nothing, so what it charges for is the discipline of naming a channel. Cheap on purpose, because
 * with one publisher and one listener it is pure overhead — it only starts paying at fan-out.
 */
export const SUBJECT_COST = 40;

/**
 * The cheapest operator in the game, because nothing earns anything until one is placed: the market
 * is only reachable from inside a pipe, so this is the tile that turns goods into money. Pricing it
 * like a luxury would deadlock the opening — there would be no way to afford the only way to earn.
 */
export const SWITCH_MAP_COST = 25;

export const SUBSCRIBER_COST = 5;
export const START_CASH = 55;

/** Charged every tick an observable is subscribed, regardless of whether it's actively delivering. */
export const SUBSCRIPTION_UPKEEP_PER_TICK = 0.01;

/** Cost lives entirely in `of`'s argument slots, see {@link ofSlotCost}. */
export const OF_PLACEMENT_COST = 0;

/** Doubles per slot already bought: 1st $2, 2nd $4, 3rd $8, ... */
export function ofSlotCost(filledCount: number): number {
  return 2 * 2 ** filledCount;
}

export const SLOT_REFUND_RATE = 0.7;

/** Bankruptcy-assistance floor for the escalating `of` slot economy. */
export const CASH_FLOOR = 20;

export const CASH_FLOOR_REGROWTH_PER_TICK = SUBSCRIPTION_UPKEEP_PER_TICK * 2;

export const INTERVAL_COST = 220;

/**
 * An `interval` never completes, so its period is the only thing separating a trickle from a
 * torrent while upkeep stays flat per tick. Pricing it inversely to the period is what stops the
 * shortest one from being strictly the best pick.
 */
export const INTERVAL_PERIOD_COST_SCALE = 4000;

export function intervalPeriodCost(periodTicks: number): number {
  return INTERVAL_PERIOD_COST_SCALE / periodTicks;
}

/**
 * Dearer than any `take(n)`, because it ends the subscription on something that happens in the
 * world rather than on a count fixed at build time — and because it is only reachable once an
 * endless `interval` is already draining upkeep, which is the bleed it exists to stop.
 */
export const TAKE_UNTIL_COST = 260;

export const TAKE_BASE_COST = 170;
export const TAKE_COST_PER_UNIT = 5;

export function takeCost(count: number): number {
  return TAKE_BASE_COST + count * TAKE_COST_PER_UNIT;
}

export function everAffordable(peakCash: number, cost: number): boolean {
  return peakCash >= cost;
}

/** Repeated sub-cent float arithmetic (e.g. per-tick upkeep) drifts; snap back to whole cents. */
export function roundCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}
